import { Box, styled } from '@mui/material';
import classnames from 'classnames';
import dayjs, { type Dayjs } from 'dayjs';

import {
  capitalizeWords,
  ECaseCardStatus,
  EMaoLegType,
  GearIcon,
  getIsItalyRegionId,
  GlowField,
  ICONS,
  ORGAN_PROGRESS_COLORS,
  parseItalianState,
} from '#shared';

import { cardWidthMap } from '../../Case';
import { ENOPCaseStatus } from '../../enums';
import { STATUS_COLORS } from '../model';

import type { ActiveCase } from '../../types';

const LABELS_MARGIN = 6;

interface Props {
  activeCase: ActiveCase & {
    updatedFields?: string[];
    isNewOnBoard?: boolean;
  };
  additionalClassName?: string;
  highlightDurationMilliseconds?: number;
  cardWidth: number;
}

const PREFIX = 'TSMED-ActiveCaseCardContentFull';

export const activeCaseCardContentFullClasses = {
  progress: `${PREFIX}-progress`,
  progressBar: `${PREFIX}-progressBar`,
  progressLine: `${PREFIX}-progressLine`,
  progressActive: `${PREFIX}-progressActive`,
  progressActiveLine: `${PREFIX}-progressActiveLine`,
  progressLabels: `${PREFIX}-progressLabels`,
  progressLabelText: `${PREFIX}-progressLabelText`,
  transports: `${PREFIX}-transports`,
  transportItem: `${PREFIX}-transportItem`,
  transportCircle: `${PREFIX}-transportCircle`,
  items: `${PREFIX}-items`,
  organIcon: `${PREFIX}-organIcon`,
  organIconEnd: `${PREFIX}-organIconEnd`,
  itemsGroup: `${PREFIX}-itemsGroup`,
  itemBig: `${PREFIX}-itemBig`,
  itemSmall: `${PREFIX}-itemSmall`,
  checkpoints: `${PREFIX}-checkpoints`,
  circle: `${PREFIX}-circle`,
  tripCardId: `${PREFIX}-tripCardId`,
  errorIcon: `${PREFIX}-errorIcon`,
  donorType: `${PREFIX}-donorType`,
  tripType: `${PREFIX}-tripType`,
  text: `${PREFIX}-text`,
  textGradient: `${PREFIX}-textGradient`,
  status: `${PREFIX}-status`,
  tripAircrafts: `${PREFIX}-tripAircrafts`,
  flightId: `${PREFIX}-flightId`,
  footer: `${PREFIX}-footer`,
  fullWidth: `${PREFIX}-fullWidth`,
};

type RootProps = {
  highlightBorder?: boolean;
  isReturningStatus?: boolean;
  cardWidth: number;
};

const excludedProps: PropertyKey[] = [
  'highlightBorder',
  'isReturningStatus',
  'cardWidth',
];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedProps.includes(prop),
})<RootProps>(
  ({ theme: { spacing, utils, palette }, isReturningStatus, cardWidth }) => ({
    [`.${activeCaseCardContentFullClasses.progressBar}`]: {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'flex-end',
      position: 'relative',
      paddingBottom: '12px',
    },
    [`.${activeCaseCardContentFullClasses.progress}`]: {
      position: 'relative',
      display: 'block',
      marginBottom: '6px',
    },
    [`.${activeCaseCardContentFullClasses.progressLine}`]: {
      height: '4px',
      width: '100%',
      borderRadius: '4px',
      backgroundColor: '#000',
    },
    [`.${activeCaseCardContentFullClasses.progressActiveLine}`]: {
      position: 'absolute',
      height: '4px',
      width: '100%',
      borderRadius: '4px',
    },
    [`.${activeCaseCardContentFullClasses.progressActive}`]: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
    [`.${activeCaseCardContentFullClasses.transports}`]: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: 11,
      top: '-14px',
    },
    [`.${activeCaseCardContentFullClasses.transportItem}`]: {
      display: 'flex',
      position: 'absolute',
      width: '30px',
      height: '30px',
      top: 0,
      zIndex: 3,
      marginLeft: 0,
      color: '#ffffff',
      transform: 'translateX(-50%)',
    },
    [`.${activeCaseCardContentFullClasses.transportCircle}`]: {
      position: 'absolute',
      alignItems: 'center',
      transform: 'translateX(-15%)',
      width: '5px',
      height: '5px',
      display: 'none',
      top: '12px',

      ['&.transportCircleShow']: {
        display: 'block',
      },

      ['& .circle']: {
        marginTop: 0,
      },
    },
    [`.${activeCaseCardContentFullClasses.items}`]: {
      height: '20px',
      width: '100%',
      bottom: '5px',
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: 10,

      ['&Organ']: {
        zIndex: 9,
      },
    },
    [`.${activeCaseCardContentFullClasses.organIcon}`]: {
      position: 'absolute',
      zIndex: 9,
      transform: 'translateX(-50%)',
      color: isReturningStatus ? palette.grey[700] : 'inherit',

      ['&End']: {
        transform: 'translateX(-100%)',
      },

      ['& > svg']: {
        width: '20px',
        height: '20px',
      },
    },
    [`.${activeCaseCardContentFullClasses.itemsGroup}`]: {
      position: 'absolute',
      bottom: '25px',
      height: '100%',
      marginLeft: 0,

      ['&Margin']: {
        marginLeft: '3.5%',
      },
    },
    [`.${activeCaseCardContentFullClasses.itemBig}`]: {
      position: 'absolute',
      display: 'flex',
      bottom: 0,
      transform: 'translateX(-50%)',
      marginLeft: '3%',

      ['&.pickUp']: {
        flexDirection: 'column',
        alignItems: 'center',
        marginLeft: 0,
      },
    },
    [`.${activeCaseCardContentFullClasses.itemSmall}`]: {
      position: 'absolute',
      display: 'flex',
      bottom: '10px',
      transform: 'translateX(-50%)',
    },
    [`.${activeCaseCardContentFullClasses.checkpoints}`]: {
      position: 'absolute',
      width: '100%',
      height: '5px',
      bottom: '12px',
    },
    [`.${activeCaseCardContentFullClasses.circle}`]: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      border: '1.5px solid #b6b6b6',
      boxSizing: 'border-box',
      position: 'absolute',
      top: '0',
      transform: 'translateX(-50%)',
    },
    [`.${activeCaseCardContentFullClasses.tripCardId}`]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
    },
    [`.${activeCaseCardContentFullClasses.progressLabels}`]: {
      position: 'relative',
      height: '20px',
      overflow: 'hidden',
    },
    [`.${activeCaseCardContentFullClasses.progressLabelText}`]: {
      fontSize: cardWidth <= cardWidthMap.LG ? '12px' : utils.clamp(12, 14),
      fontWeight: '500',
      position: 'absolute',
      display: 'flex',
    },
    [`.${activeCaseCardContentFullClasses.text}`]: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    },
    [`.${activeCaseCardContentFullClasses.textGradient}`]: {
      position: 'relative',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      textOverflow: 'clip',
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: 5,
        height: '100%',
        background:
          'linear-gradient(90deg, rgba(15, 21, 28, 0) 0%, #0F151C 100%)',
        pointerEvents: 'none',
      },
    },

    [`.${activeCaseCardContentFullClasses.tripAircrafts}`]: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing(0.5),
    },
    [`.${activeCaseCardContentFullClasses.flightId}`]: {
      padding: '2px 4px',
      borderRadius: '2px',
      height: '22px',
      backgroundColor: '#000000',
      fontFamily: "'JetBrains Mono', monospace",
      fontWeight: '500',
      fontSize: cardWidth <= cardWidthMap.LG ? '12px' : utils.clamp(12, 14),
      fontFeatureSettings: "'zero' 1",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isReturningStatus ? palette.grey[700] : 'inherit',
    },
  }),
);

const LEG_TYPE_BEFORE_DONOR_OR = [
  EMaoLegType['Empty to Donor'],
  EMaoLegType['Team To Donor'],
];
const LEG_TYPE_AFTER_DONOR_OR = [
  EMaoLegType['Team to Hub'],
  EMaoLegType['Empty to Recipient'],
  EMaoLegType['Donor to Recipient'],
];

interface AircraftPoint {
  type: EMaoLegType;
  isActive: boolean | undefined;
  isComplete: boolean | undefined;
  isBeforeDonorOr: boolean;
  registration: string;
  end: Dayjs;
}

export const ActiveCaseCardContentFull = ({
  activeCase,
  highlightDurationMilliseconds,
  cardWidth,
}: Props) => {
  const {
    points,
    organ,
    pathLabels,
    transport,
    progress,
    showReturnToBaseState,
    cardStatus,
    showOrganOnCard,
    showTransportOnCard,
    trips = [],
    donorORDateTimeUtc,
    status,
    serviceRegionId,
  } = activeCase;
  const isReturningStatus = status === ENOPCaseStatus.ReturningToBase;
  const isStatusOk = cardStatus === ECaseCardStatus.Ok;
  const organBorderColor = ORGAN_PROGRESS_COLORS[organ.name];
  const progressColor =
    (isStatusOk && STATUS_COLORS[cardStatus]) ||
    ORGAN_PROGRESS_COLORS[organ.name];
  const progressMainColor =
    cardStatus === ECaseCardStatus.Accept ? '#587E3A' : progressColor;
  const isItalian = getIsItalyRegionId(serviceRegionId ?? '');

  const TransportIcon = transport ? ICONS[transport] : null;
  const OrganIcon = ICONS[organ.name] ?? GearIcon;

  const transportProgress = progress < 96 ? progress : 96;
  // const organIconMainColor = showOrganColor ? organColor : '#6b4a4a';
  // const organIconColor = showReturnToBaseState ? '#7F8083' : organIconMainColor;
  const donorOrTime = dayjs(donorORDateTimeUtc);
  const legGroups: AircraftPoint[][] =
    trips?.map(t =>
      t.Legs.map(l => ({
        type: l.LegType,
        isActive:
          (l.Movement?.IsOffBlocks as boolean) && !l.Movement?.IsOnBlocks,
        isComplete: l.Movement?.IsOnBlocks as boolean,
        isBeforeDonorOr: dayjs(l.OnBlocksUtc).isBefore(donorOrTime),
        registration: l.Movement?.Aircraft?.Registration ?? '',
        end: dayjs(l.OnBlocksUtc),
      })),
    ) || [];
  const aircraftsBefore: AircraftPoint[] = [];
  const aircraftsAfter: AircraftPoint[] = [];

  legGroups.forEach(legs => {
    if (!legs.length) {
      return;
    }
    const leg =
      (legs.find(l => l.isActive) || legs.find(l => !l.isComplete)) ??
      (legs.at(-1) as AircraftPoint);
    if (legs.find(l => LEG_TYPE_AFTER_DONOR_OR.includes(l.type))) {
      aircraftsAfter.push(leg);
      return;
    }
    if (legs.find(l => LEG_TYPE_BEFORE_DONOR_OR.includes(l.type))) {
      aircraftsBefore.push(leg);
      return;
    }

    aircraftsAfter.push(leg);
  });

  return (
    <Root isReturningStatus={isReturningStatus} cardWidth={cardWidth}>
      <Box className={activeCaseCardContentFullClasses.progress}>
        <Box className={activeCaseCardContentFullClasses.progressBar}>
          <Box className={activeCaseCardContentFullClasses.progressLine} />
          <Box
            className={activeCaseCardContentFullClasses.progressActiveLine}
            style={{
              width: `${progress}%`,
              backgroundColor: !showReturnToBaseState
                ? progressMainColor
                : '#7F8083B2',
            }}
          />
          <Box className={activeCaseCardContentFullClasses.progressActive}>
            {showOrganOnCard && (
              <Box className={activeCaseCardContentFullClasses.items}>
                <GlowField
                  startColor={organBorderColor}
                  ms={highlightDurationMilliseconds}
                  className={classnames(
                    activeCaseCardContentFullClasses.organIcon,
                    {
                      [activeCaseCardContentFullClasses.organIconEnd]:
                        organ.startPoint === 100,
                    },
                  )}
                  style={{ left: `${organ.startPoint}%` }}
                  deps={[organ.name]}
                >
                  <OrganIcon />
                </GlowField>
              </Box>
            )}

            {showTransportOnCard && !!transport && !!TransportIcon && (
              <Box className={activeCaseCardContentFullClasses.transports}>
                <GlowField
                  className={activeCaseCardContentFullClasses.transportItem}
                  ms={highlightDurationMilliseconds}
                  style={{
                    left: `${transportProgress}%`,
                    transform: `translate(${progress < 3 ? -15 : -50}%)`,
                  }}
                  component={TransportIcon}
                  deps={[TransportIcon]}
                />
              </Box>
            )}

            <Box className={activeCaseCardContentFullClasses.checkpoints}>
              {points.map(point =>
                !point.hidden ? (
                  <Box
                    key={point.id}
                    className={activeCaseCardContentFullClasses.circle}
                    style={{ left: `${point.point}%` }}
                  />
                ) : null,
              )}
            </Box>
          </Box>
        </Box>

        <Box className={activeCaseCardContentFullClasses.progressLabels}>
          {pathLabels.map((label, index) => {
            const isStart = !label.startPoint;
            const isDonorOr = index === 1;
            const isTmdxAviationCase = isDonorOr && label.startPoint === 10;
            const donorOrMaxWidth = isTmdxAviationCase
              ? pathLabels[2].startPoint - label.startPoint
              : pathLabels[2].startPoint - label.startPoint / 2;
            const isEnd = label.startPoint === 100;
            const names = label.name.split(', ');
            const isLeftTitle = isStart || isEnd || isTmdxAviationCase;

            return (
              <Box
                key={label.id}
                className={activeCaseCardContentFullClasses.progressLabelText}
                style={{
                  color: !label.isActive ? '#87888B' : '#DDDDDD',
                  maxWidth: isDonorOr
                    ? `${donorOrMaxWidth - LABELS_MARGIN}%`
                    : 'auto',
                  width: 'auto',
                  left: isEnd ? 'auto' : `${label.startPoint}%`,
                  transform: `translate(${isLeftTitle ? 0 : '-50'}%)`,
                  right: isEnd ? '0' : `auto`,
                }}
              >
                {names.map((i, index) => {
                  const isCity = names.length === 2 && index === 0;
                  const stateText = isItalian
                    ? parseItalianState(i)
                    : i.toUpperCase();
                  const text = isCity ? capitalizeWords(i) : stateText;

                  return (
                    <GlowField
                      key={`${label.id}-${index}`}
                      className={classnames(
                        activeCaseCardContentFullClasses.text,
                        isCity && activeCaseCardContentFullClasses.textGradient,
                      )}
                      ms={highlightDurationMilliseconds}
                      style={{
                        minWidth: names.length > 1 && index === 0 ? 0 : 'auto',
                        flexShrink: names.length > 1 && index === 1 ? 0 : 1,
                      }}
                      deps={[i]}
                    >
                      {index && names.length > 1 ? `,${text}` : text}
                    </GlowField>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box className={activeCaseCardContentFullClasses.tripAircrafts}>
        {aircraftsBefore
          .sort((a, b) => (a.end.isBefore(b.end) ? -1 : 1))
          .map((l, i) => (
            <GlowField
              startColor={organBorderColor}
              key={`${l.end.valueOf()}-${i}`}
              className={activeCaseCardContentFullClasses.flightId}
              ms={highlightDurationMilliseconds}
              deps={[l.registration]}
            >
              {l.registration}
            </GlowField>
          ))}
        <Box style={{ flexGrow: 1 }} />
        {aircraftsAfter
          .sort((a, b) => (a.end.isBefore(b.end) ? -1 : 1))
          .map((l, i) => (
            <GlowField
              startColor={organBorderColor}
              key={`${l.end.valueOf()}-${i}`}
              className={activeCaseCardContentFullClasses.flightId}
              ms={highlightDurationMilliseconds}
              deps={[l.registration]}
            >
              {l.registration}
            </GlowField>
          ))}
      </Box>
    </Root>
  );
};
